import { useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";

const STATS = [
  { id: "stat-years", value: "10+", label: "Years Experience" },
  { id: "stat-projects", value: "250+", label: "Projects Completed" },
  { id: "stat-satisfaction", value: "100%", label: "Client Satisfaction" },
  { id: "stat-referrals", value: "90%", label: "Referral Rate" }
];

const StatsSection = () => (
  <section className="bg-slate-900 py-12">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {STATS.map((stat) => (
          <div key={stat.id} className="text-center">
            <p className="text-4xl md:text-5xl font-black text-amber-500">{stat.value}</p>
            <p className="text-stone-400 text-sm mt-2">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const EstimatorSection = ({ projectTypes, loading, formatPrice, onProjectSelect }) => (
  <section id="estimator" className="py-20 md:py-32 bg-gradient-to-br from-stone-100 to-stone-200">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="text-center mb-16">
        <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
          Instant Quote Calculator
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Get Your Free Estimate
        </h2>
        <p className="text-lg text-stone-600 max-w-2xl mx-auto">
          Select your project type to receive an instant ballpark estimate. 
          No obligation, just transparent pricing to help you plan.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projectTypes.map((project, index) => (
            <div
              key={project.id}
              onClick={() => onProjectSelect(project.id)}
              className="project-card cursor-pointer bg-white border-2 border-transparent hover:border-amber-500/50 rounded-lg overflow-hidden shadow-xl shadow-stone-200/50 group animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`project-card-${project.id}`}
            >
              <div className="relative h-48 overflow-hidden bg-stone-200">
                <img
                  src={project.image_url}
                  alt={project.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                  width={400}
                  height={192}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white/80 text-sm font-medium">Starting from</p>
                  <p className="text-white text-2xl font-bold">
                    {formatPrice(project.base_price_min, project.base_price_max)}
                  </p>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-amber-700 transition-colors">
                  {project.name}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  {project.description}
                </p>
                <div className="flex items-center text-amber-700 font-semibold text-sm">
                  Get Estimate
                  <ArrowRight className="ml-2 transition-transform group-hover:translate-x-2" size={16} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

const CTASection = () => {
  const scrollToEstimator = useCallback(() => {
    document.getElementById('estimator')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section className="py-20 md:py-32 bg-slate-900">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
          Ready to Start Your Project?
        </h2>
        <p className="text-lg text-stone-300 mb-8 max-w-2xl mx-auto">
          Get in touch today for a free, no-obligation estimate. We will discuss 
          your requirements and provide a competitive fixed-price quote.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={scrollToEstimator}
            className="bg-amber-700 text-white hover:bg-amber-600 h-14 px-10 font-bold shadow-lg"
            data-testid="cta-get-quote-btn"
          >
            Get Free Estimate
            <ArrowRight className="ml-2" size={20} />
          </Button>
          <Link to="/contact">
            <Button
              variant="outline"
              className="h-14 px-8 font-semibold border-2 border-white text-white hover:bg-white hover:text-slate-900"
            >
              <Phone className="mr-2" size={20} />
              Contact Us
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export { StatsSection, EstimatorSection, CTASection };
